import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import {
  cursorAtom,
  groupNodeCreateStartAtom,
  nodesAtom,
  selectedNodesAtom,
  selectedNodeTypeAtom,
  getEventRawText,
  saveCanvasAsyncCommand,
} from "@zapier/canvas-state";
import { Node, useReactFlow } from "@xyflow/react";
import { DRAG_AND_DROP } from "@zapier/canvas-constants";

import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";
import useGetNodeDataByTypeFn from "../../hooks/useGetNodeDataByTypeFn";
import { sortNodes } from "../../utils/sortNodes";

export function useOnSelection() {
  const { screenToFlowPosition } = useReactFlow();
  const getNodeDataByType = useGetNodeDataByTypeFn();
  const selectedNodeType = useAtomValue(selectedNodeTypeAtom);
  const { emitEventOnAddStep } = useEmitUserInteractionEvents();

  const onSelectionStart = useAtomCallback(
    useCallback((get, set, event: React.MouseEvent) => {
      const cursorMode = get(cursorAtom);
      if (cursorMode === "add-group") {
        set(groupNodeCreateStartAtom, {
          x: event.clientX,
          y: event.clientY,
        });
      }
    }, []),
  );

  const onSelectionEnd = useAtomCallback(
    useCallback(
      async (get, set, event: React.MouseEvent) => {
        const cursorMode = get(cursorAtom);
        const groupNodeCreateStart = get(groupNodeCreateStartAtom);
        const nodes = get(nodesAtom);
        const selectedNodes = get(selectedNodesAtom);
        // For now we don't want group nodes to capture other group nodes
        const selectedNodesNoGroupNodes = selectedNodes.filter(
          (n) => n.type !== "group",
        );
        // We only want to group nodes that don't have a parent node
        // This is so a new group node doesn't steal children from an existing group node
        // Zap step nodes have the zap node id as its parentId. We need to exclude zap steps or else it breaks the zap step rendering completely since grouping them will change the parentId and position.
        const selectedNodesWithNoParent = selectedNodesNoGroupNodes.filter(
          (n) => !n.parentId,
        );

        if (
          cursorMode === "add-group" &&
          groupNodeCreateStart?.x &&
          groupNodeCreateStart?.y
        ) {
          const positionStart = screenToFlowPosition({
            x: groupNodeCreateStart.x,
            y: groupNodeCreateStart.y,
          });
          const positionEnd = screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          });
          const height = Math.abs(positionEnd.y - positionStart.y);
          const width = Math.abs(positionEnd.x - positionStart.x);
          // Calculate the top-left corner of the rectangle
          const x = Math.min(positionStart.x, positionEnd.x);
          const y = Math.min(positionStart.y, positionEnd.y);

          const newGroupNode = getNodeDataByType({
            type: "group",
            // TODO: Handle the case where the user drags from right to left
            position: {
              // TODO: Figure out why we need to adjust it ~55px each direction
              x: x + 50,
              y: y + 50,
            },
            // If the user selects a really tiny space, or even just clicks and triggers a 1x1 group node,
            // This ensures that they don't accidentally create a group node that is too small to see
            width: Math.max(width, 200),
            height: Math.max(height, 200),
          }) as Node;

          const selectedNodeIds = selectedNodesWithNoParent.map((n) => n.id);
          const newNodesWithGroupNode = nodes
            .concat(newGroupNode)
            .sort(sortNodes);
          const nodesWithParentGroupNode = newNodesWithGroupNode.map((n) => {
            if (selectedNodeIds.includes(n.id)) {
              return {
                ...n,
                parentId: newGroupNode.id,
                expandParent: true,
                extent: "parent",
                selected: false,
              } as Node;
            }

            return {
              ...n,
              selected: false,
            };
          });

          const rawText = getEventRawText(selectedNodeType);
          emitEventOnAddStep(DRAG_AND_DROP, rawText);

          set(nodesAtom, nodesWithParentGroupNode);
          set(saveCanvasAsyncCommand, {
            nodes: nodesWithParentGroupNode,
          });
          set(cursorAtom, "select");
          set(groupNodeCreateStartAtom, null);
          set(selectedNodeTypeAtom, null);
        }
      },
      [
        screenToFlowPosition,
        getNodeDataByType,
        selectedNodeType,
        emitEventOnAddStep,
      ],
    ),
  );
  return { onSelectionStart, onSelectionEnd };
}
